'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { CONTACT_PHONE, CONTACT_PHONE_TEL, INSTAGRAM_DM_URL } from '@/lib/policies';

// Shown when a page cannot render, typically a cold cache while the catalog
// service is unreachable (a warm page keeps serving its last good copy).
// Never shows platform text; always offers a way to order anyway.
export default function Error({ error, retry }: { error: Error & { digest?: string }; retry: () => void }) {
  useEffect(() => {
    console.error('[app error]', error.digest ?? '', error.message);
  }, [error]);

  return (
    <div className="bg-bone min-h-[70vh] flex items-center justify-center px-6 py-20">
      <div className="max-w-md mx-auto text-center">
        <h1 className="font-display tracking-[-0.01em] font-semibold text-3xl md:text-4xl text-ink mb-4">
          Something slipped
        </h1>
        <p className="text-ink/65 mb-8 leading-relaxed">
          We couldn&apos;t load this page just now. Try again in a moment. You can still order with us directly: call or text{' '}
          <a href={CONTACT_PHONE_TEL} className="underline underline-offset-2 font-numerals">{CONTACT_PHONE}</a>, or{' '}
          <a href={INSTAGRAM_DM_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">message us on Instagram</a>.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={() => retry()}
            className="bg-plum text-white px-8 py-3.5 text-xs tracking-[0.12em] uppercase font-medium hover:bg-plum-deep transition-colors rounded-sm"
          >
            Try again
          </button>
          <Link
            href="/"
            className="border border-ink/30 text-ink px-8 py-3.5 text-xs tracking-[0.12em] uppercase font-medium hover:border-ink transition-colors rounded-full"
          >
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
}
